import { TextField, InputAdornment, IconButton } from "@mui/material"
import type { OutlinedTextFieldProps, OutlinedInputProps, InputBaseComponentProps } from "@mui/material"
import { useFormikContext } from "formik"
import { Plus, Minus, User } from "lucide-mui"
import React from "react"

interface PublicPeopleAmountInputProps {
  name: string
  label?: string
  min?: number
  max?: number
  disabled?: boolean
  size?: OutlinedTextFieldProps["size"]
  className?: string
  slotProps?: {
    input?: Partial<OutlinedInputProps>
    htmlInput?: Partial<InputBaseComponentProps>
    [key: string]: any
  }
  displayIcon?: boolean
}

const PublicPeopleAmountInput: React.FC<PublicPeopleAmountInputProps> = ({
  name,
  label,
  min = 1,
  max = 20,
  disabled = false,
  size = "medium",
  className,
  slotProps,
  displayIcon = false,
}) => {
  const { values, setFieldValue, setFieldTouched } = useFormikContext<any>()
  const value = values[name]

  const handleIncrement = () => {
    const currentValue = value === "" || value === null || value === undefined ? 0 : Number(value)
    if (!Number.isFinite(currentValue) || currentValue >= max) return
    void setFieldValue(name, currentValue + 1)
  }

  const handleDecrement = () => {
    const currentValue = value === "" || value === null || value === undefined ? 0 : Number(value)
    if (!Number.isFinite(currentValue) || currentValue <= min) return
    void setFieldValue(name, currentValue - 1)
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value
    if (inputValue === "") {
      void setFieldValue(name, "")
    } else {
      const newValue = parseInt(inputValue, 10)
      if (!isNaN(newValue) && newValue >= 0 && newValue <= max) {
        void setFieldValue(name, newValue)
      }
    }
  }

  const handleBlur = () => {
    void setFieldTouched(name, true)
  }

  const displayValue = value === "" || value === null || value === undefined ? "" : value
  const currentValue = value === "" || value === null || value === undefined ? 0 : Number(value)

  return (
    <TextField
      label={label}
      value={displayValue}
      onChange={handleChange}
      onBlur={handleBlur}
      disabled={disabled}
      fullWidth
      size={size}
      className={className}
      type="number"
      data-testid="public-venue-people-input-container"
      slotProps={{
        ...slotProps,
        inputLabel: {
          shrink: true,
          ...slotProps?.inputLabel,
        },
        input: {
          ...slotProps?.input,
          startAdornment: (
            <>
              {displayIcon ? (
                <InputAdornment position="start">
                  <User className="w-4 h-4 text-gray-400" />
                </InputAdornment>
              ) : null}
              <InputAdornment position="start">
                <IconButton
                  onClick={handleDecrement}
                  disabled={disabled || !Number.isFinite(currentValue) || currentValue <= min}
                  edge="start"
                  size="small"
                  aria-label="decrease people amount"
                  data-testid="public-venue-people-input-decrement"
                >
                  <Minus className="w-3.5 h-3.5" />
                </IconButton>
              </InputAdornment>
              {slotProps?.input?.startAdornment}
            </>
          ),
          endAdornment: (
            <>
              {slotProps?.input?.endAdornment}
              <InputAdornment position="end">
                <IconButton
                  onClick={handleIncrement}
                  disabled={disabled || !Number.isFinite(currentValue) || currentValue >= max}
                  edge="end"
                  size="small"
                  aria-label="increase people amount"
                  data-testid="public-venue-people-input-increment"
                >
                  <Plus className="w-3.5 h-3.5" />
                </IconButton>
              </InputAdornment>
            </>
          ),
        },
        htmlInput: {
          ...slotProps?.htmlInput,
          min,
          max,
          style: { textAlign: "center", ...slotProps?.htmlInput?.style },
          className:
            "[appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none text-center",
        },
      }}
    />
  )
}

export default PublicPeopleAmountInput
